import { muiTheme } from "@/styles/theme";
import { ConnectorPosition } from "../types";

const MIN_SPACING = 120;
const EXTRA_SPACING_FOR_DUAL_TARGETS = 60; // Extra space for nodes with both target types
const BASE_HEIGHT = 70;
const VISUAL_PADDING = 84; // Keep the value that was working properly

// Helper to check if a node has both team and personal targets with non-zero values
const hasBothTargetTypes = (targetsData?: any[]): boolean => {
  if (!targetsData || targetsData.length === 0) return false;
  const hasTeam = targetsData.some(
    (d) => d.targetType === "team" && parseFloat(d.target.replace(/,/g, "")) > 0
  );
  const hasPersonal = targetsData.some(
    (d) =>
      d.targetType === "personal" &&
      (parseFloat(d.target.replace(/,/g, "")) > 0 ||
        parseFloat(d.current.replace(/,/g, "")) > 0)
  );
  return hasTeam && hasPersonal;
};

// New helper to calculate base positions with dynamic spacing
const calculateBasePositions = (
  children: ConnectorPosition[],
  parentHasDualTargets: boolean = false
): number[] => {
  const positions: number[] = [];
  if (children.length === 0) {
    return positions;
  }

  // Start position for the first child
  let currentPos = parentHasDualTargets ? 160 : 40;
  positions.push(currentPos);

  // Calculate cumulative position for subsequent children
  for (let i = 0; i < children.length - 1; i++) {
    const prevChild = children[i];
    const extraSpacing = hasBothTargetTypes(prevChild.targetsData)
      ? EXTRA_SPACING_FOR_DUAL_TARGETS
      : 0;
    currentPos += MIN_SPACING + extraSpacing;
    positions.push(currentPos);
  }
  return positions;
};

export const calculateChildrenPositions = (
  children: ConnectorPosition[],
  parentHasDualTargets: boolean = false
): ConnectorPosition[] => {
  const basePositions = calculateBasePositions(children, parentHasDualTargets);
  return children.map((child, index) => ({
    ...child,
    position: basePositions[index],
  }));
};

export const calculateDynamicChildrenSpacing = (
  children: ConnectorPosition[],
  expandedStates: Record<string, boolean>,
  parentHasDualTargets: boolean = false
): number => {
  if (children.length === 0) return 0;

  const totalHeight = calculateActualEndPosition(
    children,
    expandedStates,
    parentHasDualTargets
  );
  return Math.max(0, totalHeight - BASE_HEIGHT);
};

export const calculateDynamicPositions = (
  connectors: ConnectorPosition[],
  expandedStates: Record<string, boolean>
): ConnectorPosition[] => {
  let cumulativeOffset = 0;

  const result = connectors.map((connector, index) => {
    // Check if this connector needs extra space due to sibling above
    if (index > 0) {
      const siblingAbove = connectors[index - 1];

      // Check if sibling above is expanded and has children
      if (
        expandedStates[siblingAbove.username] &&
        siblingAbove.children?.length
      ) {
        const lastChildOfSibling =
          siblingAbove.children[siblingAbove.children.length - 1];

        // Add extra spacing between last child of expanded parent and next sibling
        let extraSpacingForLastChild = 40; // Base extra spacing

        // If the last child has personal targets (dual targets), add even more spacing
        if (
          lastChildOfSibling &&
          hasBothTargetTypes(lastChildOfSibling.targetsData)
        ) {
          extraSpacingForLastChild += 30; // Additional spacing for dual targets
        }

        cumulativeOffset += extraSpacingForLastChild;

        // If last child of sibling above has reports
        if (lastChildOfSibling?.hasChildren) {
          // Check if the last child is expanded
          if (
            expandedStates[lastChildOfSibling.username] &&
            lastChildOfSibling.children?.length
          ) {
            // Calculate the actual space needed for the expanded last child
            const lastChildHeight = calculateActualEndPosition(
              lastChildOfSibling.children,
              expandedStates,
              false
            );
            cumulativeOffset += lastChildHeight;
          } else {
            // If last child is not expanded but has children, use base height for thread + plus icon
            cumulativeOffset += BASE_HEIGHT;
          }
        }
      }
    }

    const newPosition = connector.position + cumulativeOffset;

    if (expandedStates[connector.username] && connector.children?.length) {
      const childrenHeight = calculateActualEndPosition(
        connector.children,
        expandedStates,
        false
      );

      const additionalOffset = Math.max(0, childrenHeight - BASE_HEIGHT);
      cumulativeOffset += additionalOffset;
    }

    return { ...connector, position: newPosition };
  });

  return result;
};

export const calculateActualEndPosition = (
  children: ConnectorPosition[],
  expandedStates: Record<string, boolean>,
  parentHasDualTargets: boolean = false
): number => {
  if (children.length === 0) return BASE_HEIGHT;

  let maxPosition = 0;
  let cumulativeOffset = 0;

  const basePositions = calculateBasePositions(children, parentHasDualTargets);

  children.forEach((child, index) => {
    // Check if this child needs extra space due to sibling above
    if (index > 0) {
      const siblingAbove = children[index - 1];

      // Check if sibling above is expanded and has children
      if (
        expandedStates[siblingAbove.username] &&
        siblingAbove.children?.length
      ) {
        const lastChildOfSibling =
          siblingAbove.children[siblingAbove.children.length - 1];

        // Add extra spacing between last child of expanded parent and next sibling (same as in calculateDynamicPositions)
        let extraSpacingForLastChild = 70; // Base extra spacing

        // If the last child has personal targets (dual targets), add even more spacing
        if (
          lastChildOfSibling &&
          hasBothTargetTypes(lastChildOfSibling.targetsData)
        ) {
          extraSpacingForLastChild += 10; // Additional spacing for dual targets
        }

        cumulativeOffset += extraSpacingForLastChild;

        // If last child of sibling above has reports
        if (lastChildOfSibling?.hasChildren) {
          // Check if the last child is expanded
          if (
            expandedStates[lastChildOfSibling.username] &&
            lastChildOfSibling.children?.length
          ) {
            // Calculate the actual space needed for the expanded last child
            const lastChildHeight = calculateActualEndPosition(
              lastChildOfSibling.children,
              expandedStates,
              false
            );
            cumulativeOffset += lastChildHeight;
          } else {
            // If last child is not expanded but has children, use base height for thread + plus icon
            cumulativeOffset += BASE_HEIGHT;
          }
        }
      }
    }

    const basePosition = basePositions[index];
    const actualPosition = basePosition + cumulativeOffset;
    maxPosition = Math.max(maxPosition, actualPosition);

    if (expandedStates[child.username] && child.children?.length) {
      const childHeight = calculateActualEndPosition(
        child.children,
        expandedStates,
        false
      );
      cumulativeOffset += Math.max(0, childHeight - BASE_HEIGHT);
    }
  });

  // Add extra spacing at the end if the last child has dual targets
  const lastChild = children[children.length - 1];
  let extraEndSpacing = 100; // Base end spacing

  if (lastChild && hasBothTargetTypes(lastChild.targetsData)) {
    extraEndSpacing += 50; // Additional spacing for last child with dual targets
  }

  return maxPosition + extraEndSpacing;
};

export const calculateThreadHeight = (
  connectors: ConnectorPosition[],
  expandedStates: Record<string, boolean>,
  isExpanded: boolean,
  parentHasDualTargets: boolean = false
): number => {
  if (!isExpanded || connectors.length === 0) return BASE_HEIGHT;

  // Calculate thread height without the extra end spacing for dual targets
  let maxPosition = 0;
  let cumulativeOffset = 0;

  const basePositions = calculateBasePositions(
    connectors,
    parentHasDualTargets
  );

  connectors.forEach((child, index) => {
    // Check if this child needs extra space due to sibling above
    if (index > 0) {
      const siblingAbove = connectors[index - 1];

      // Check if sibling above is expanded and has children
      if (
        expandedStates[siblingAbove.username] &&
        siblingAbove.children?.length
      ) {
        const lastChildOfSibling =
          siblingAbove.children[siblingAbove.children.length - 1];

        // Add extra spacing between last child of expanded parent and next sibling
        let extraSpacingForLastChild = 70; // Base extra spacing

        // If the last child has personal targets (dual targets), add even more spacing
        if (
          lastChildOfSibling &&
          hasBothTargetTypes(lastChildOfSibling.targetsData)
        ) {
          extraSpacingForLastChild += 10; // Additional spacing for dual targets
        }

        cumulativeOffset += extraSpacingForLastChild;

        // If last child of sibling above has reports
        if (lastChildOfSibling?.hasChildren) {
          // Check if the last child is expanded
          if (
            expandedStates[lastChildOfSibling.username] &&
            lastChildOfSibling.children?.length
          ) {
            // Calculate the actual space needed for the expanded last child
            const lastChildHeight = calculateActualEndPosition(
              lastChildOfSibling.children,
              expandedStates,
              false
            );
            cumulativeOffset += lastChildHeight;
          } else {
            // If last child is not expanded but has children, use base height for thread + plus icon
            cumulativeOffset += BASE_HEIGHT;
          }
        }
      }
    }

    const basePosition = basePositions[index];
    const actualPosition = basePosition + cumulativeOffset;
    maxPosition = Math.max(maxPosition, actualPosition);

    if (expandedStates[child.username] && child.children?.length) {
      const childHeight = calculateActualEndPosition(
        child.children,
        expandedStates,
        false
      );
      cumulativeOffset += Math.max(0, childHeight - BASE_HEIGHT);
    }
  });

  // Thread height should reach the last child + base spacing, but not include extra dual target spacing
  const threadHeight = maxPosition + 100; // Base spacing only

  return threadHeight - VISUAL_PADDING;
};

export const getAllDescendantUsernames = (
  children: ConnectorPosition[]
): string[] => {
  const descendants: string[] = [];

  children.forEach((child) => {
    descendants.push(child.username);
    if (child.children?.length) {
      descendants.push(...getAllDescendantUsernames(child.children));
    }
  });

  return descendants;
};

export function getProgressColor(progressPercentage: number): string {
  const percentage = Math.round(progressPercentage * 100);

  if (percentage >= 100) {
    return muiTheme.colorSchemes.light.palette.success[400];
  } else if (percentage >= 70) {
    return muiTheme.colorSchemes.light.palette.warning[400];
  } else {
    return muiTheme.colorSchemes.light.palette.error[500];
  }
}
